import { useCallback, useEffect, useState } from 'react';
import { supabase, type Student } from '@/lib/supabase';

const GRADE_OPTIONS = ['초1', '초2', '초3', '초4', '초5', '초6', '중1', '중2', '중3', '고1', '고2', '고3', '재수/N수'];

interface StudentForm {
  name: string;
  grade: string;
  school: string;
  parent_name: string;
  parent_phone: string;
  notes: string;
  login_id: string;
  password: string;
}

const emptyForm: StudentForm = {
  name: '',
  grade: '고1',
  school: '',
  parent_name: '',
  parent_phone: '',
  notes: '',
  login_id: '',
  password: '',
};

export default function StudentManager({
  students,
  onStudentsChange,
  selectedId,
  onSelect,
  maxStudents,
}: {
  students: Student[];
  onStudentsChange: () => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  maxStudents: number;
}) {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Student | null>(null);
  const [form, setForm] = useState<StudentForm>(emptyForm);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (student: Student) => {
    setEditing(student);
    setForm({
      name: student.name,
      grade: student.grade || '고1',
      school: student.school || '',
      parent_name: student.parent_name || '',
      parent_phone: student.parent_phone || '',
      notes: student.notes || '',
      login_id: student.login_id || '',
      password: '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.school.trim()) {
      setError('이름과 학교는 필수 입력 항목입니다.');
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        const { error: err } = await supabase
          .from('students')
          .update({
            name: form.name.trim(),
            grade: form.grade,
            school: form.school.trim(),
            parent_name: form.parent_name.trim() || null,
            parent_phone: form.parent_phone.trim() || null,
            notes: form.notes.trim() || null,
          })
          .eq('id', editing.id);
        if (err) throw err;
      } else {
        if (students.length >= maxStudents) {
          setError(`학원 정원(${maxStudents}명)이 가득 찼습니다. 정원 증설이 필요합니다.`);
          setBusy(false);
          return;
        }
        if (!form.login_id.trim()) {
          setError('학생이 로그인할 아이디를 입력해주세요.');
          setBusy(false);
          return;
        }
        if (form.password.length < 6) {
          setError('비밀번호는 6자 이상이어야 합니다.');
          setBusy(false);
          return;
        }
        const { data, error: fnErr } = await supabase.functions.invoke('create-student-account', {
          body: {
            login_id: form.login_id.trim(),
            password: form.password,
            name: form.name.trim(),
            grade: form.grade,
            school: form.school.trim(),
            parent_name: form.parent_name.trim() || null,
            parent_phone: form.parent_phone.trim() || null,
          },
        });

        // 엣지함수가 4xx/5xx 응답 시 실제 서버 메시지를 꺼내서 보여줌
        if (fnErr) {
          let serverMsg = '';
          let alreadyExists = false;
          try {
            const context = (fnErr as { context?: { json: () => Promise<unknown> } }).context;
            if (context && typeof context.json === 'function') {
              const serverData = (await context.json()) as {
                success?: boolean;
                message?: string;
                alreadyExists?: boolean;
              } | null;
              if (serverData?.message) serverMsg = serverData.message;
              if (serverData?.alreadyExists) alreadyExists = true;
            }
          } catch {
            serverMsg = '';
          }
          setError(serverMsg || (alreadyExists ? '중복된 아이디입니다.' : '학생 등록 중 오류가 발생했습니다.'));
          setBusy(false);
          return;
        }

        if (!data?.success) {
          setError(data?.alreadyExists ? '중복된 아이디입니다.' : (data?.message || '학생 등록 중 오류가 발생했습니다.'));
          setBusy(false);
          return;
        }
      }
      setShowModal(false);
      onStudentsChange();
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      setError(msg || '저장 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (student: Student) => {
    if (!window.confirm(`'${student.name}' 학생을 삭제하시겠습니까? 관련 검사 기록도 함께 삭제됩니다.`)) {
      return;
    }
    try {
      const { error: err } = await supabase.from('students').delete().eq('id', student.id);
      if (err) throw err;
      if (selectedId === student.id) onSelect('');
      onStudentsChange();
    } catch (e) {
      window.alert('삭제 중 오류가 발생했습니다.');
      console.error(e);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-lg font-bold text-foreground-950">학생 관리</h2>
        <button
          onClick={openAdd}
          className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer"
        >
          <i className="ri-add-line"></i>
          학생 추가
        </button>
      </div>

      <div className="bg-white rounded-xl border border-background-200 divide-y divide-background-100">
        {students.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-foreground-500 text-sm mb-2">아직 등록된 학생이 없습니다.</div>
            <button
              onClick={openAdd}
              className="text-primary-600 hover:text-primary-700 text-sm font-semibold cursor-pointer"
            >
              첫 학생을 추가해보세요
            </button>
          </div>
        ) : (
          students.map((student) => (
            <div
              key={student.id}
              onClick={() => onSelect(student.id)}
              className={`p-4 flex items-center gap-4 cursor-pointer transition-colors ${
                selectedId === student.id ? 'bg-primary-50' : 'hover:bg-background-50'
              }`}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 font-semibold flex-shrink-0">
                {student.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-foreground-950 text-sm">{student.name}</div>
                <div className="text-xs text-foreground-500 flex items-center gap-1.5 flex-wrap">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-background-100 text-foreground-600">
                    {student.grade || '학년 미정'}
                  </span>
                  <span>{student.school || '학교 미기입'}</span>
                  {student.login_id && (
                    <span className="inline-flex items-center gap-1 text-primary-700">
                      <i className="ri-user-line"></i>
                      {student.login_id}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => openEdit(student)}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
                  title="수정"
                >
                  <i className="ri-edit-line"></i>
                </button>
                <button
                  onClick={() => handleDelete(student)}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  title="삭제"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg font-bold text-foreground-950">
                {editing ? '학생 정보 수정' : '학생 추가'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm font-medium text-foreground-700 mb-1.5">이름 *</span>
                  <input
                    type="text"
                    name="name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-foreground-700 mb-1.5">학년</span>
                  <select
                    name="grade"
                    value={form.grade}
                    onChange={(e) => setForm({ ...form, grade: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  >
                    {GRADE_OPTIONS.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="block">
                <span className="block text-sm font-medium text-foreground-700 mb-1.5">학교 *</span>
                <input
                  type="text"
                  name="school"
                  required
                  value={form.school}
                  onChange={(e) => setForm({ ...form, school: e.target.value })}
                  className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  placeholder="예: 서울○○고등학교"
                />
              </label>

              {!editing && (
                <>
                  <div className="rounded-md bg-accent-50 border border-accent-200 px-3 py-2.5 text-xs text-foreground-700">
                    <div className="flex items-center gap-1.5 font-semibold text-foreground-900 mb-1">
                      <i className="ri-key-2-line"></i>
                      학생 로그인 계정
                    </div>
                    학생은 아래 아이디·비밀번호로 로그인해 검사를 진행합니다. 학원 이용기간 동안 사용할 수 있어요.
                  </div>

                  <label className="block">
                    <span className="block text-sm font-medium text-foreground-700 mb-1.5">
                      로그인 아이디 *
                    </span>
                    <input
                      type="text"
                      name="login_id"
                      required
                      value={form.login_id}
                      onChange={(e) => setForm({ ...form, login_id: e.target.value })}
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                      placeholder="영문·숫자 3~20자 (예: kim01)"
                    />
                  </label>

                  <label className="block">
                    <span className="block text-sm font-medium text-foreground-700 mb-1.5">
                      비밀번호 *
                    </span>
                    <input
                      type="password"
                      name="password"
                      required
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                      placeholder="6자 이상"
                    />
                  </label>
                </>
              )}

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm font-medium text-foreground-700 mb-1.5">
                    보호자 성함
                  </span>
                  <input
                    type="text"
                    name="parent_name"
                    value={form.parent_name}
                    onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-foreground-700 mb-1.5">
                    보호자 연락처
                  </span>
                  <input
                    type="text"
                    name="parent_phone"
                    value={form.parent_phone}
                    onChange={(e) => setForm({ ...form, parent_phone: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </label>
              </div>

              <label className="block">
                <span className="block text-sm font-medium text-foreground-700 mb-1.5">비고</span>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  maxLength={500}
                  className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  rows={2}
                />
              </label>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {error}
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="whitespace-nowrap px-4 py-2 text-sm rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-5 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                >
                  {busy ? '저장 중...' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}